/**
 * CSV Utility Functions
 * Contains reusable functions for CSV export functionality
 */

/**
 * Converts an array of objects to CSV string format
 * @param {Array} data - Array of objects to convert
 * @returns {string} - CSV formatted string
 */
export const convertToCSV = (data) => {
  if (!data || !data.length) return "";

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header] || "";
          // Escape quotes and wrap in quotes if needed
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(",")
    ),
  ];

  return csvRows.join("\n");
};

/**
 * Downloads CSV content as a file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Formats delivery methods for CSV export
 * @param {Array} deliveryMethods - Array of delivery method strings
 * @returns {string} - Formatted delivery methods string
 */
export const formatDeliveryMethods = (deliveryMethods) => {
  if (!deliveryMethods || !deliveryMethods.length) {
    return "-";
  }

  if (deliveryMethods.includes("noknok")) {
    const formatted = deliveryMethods.map((method) => {
      if (method === "noknok") {
        return "External Provider";
      }
      return method;
    });
    return formatted.join(" - ");
  }

  const formatted = deliveryMethods.map((method) => {
    if (method.toLowerCase() === "grms") {
      return "GRMS";
    }
    if (method.toLowerCase() === "whatsapp") {
      return "Whatsapp";
    }
    if (method.toLowerCase() === "email") {
      return "Email";
    }
    return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
  });

  return formatted.join(" - ");
};

/**
 * Formats status for CSV export
 * @param {string} status - Status value
 * @param {Array} deliveryMethods - Array of delivery methods
 * @returns {string} - Formatted status string
 */
export const formatStatus = (status, deliveryMethods) => {
  if (deliveryMethods && deliveryMethods.includes("grms")) {
    return "-";
  }

  const statusDisplayOptions = [
    { value: "received", label: "Received" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "paymentPending", label: "Payment Pending" },
    { value: "delivering", label: "Delivering" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
    { value: "succeeded", label: "Succeeded" },
  ];

  const option = statusDisplayOptions.find((opt) => opt.value === status);

  if (option) {
    return option.label;
  }
  if (status) {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }
  return "Pending";
};

/**
 * Exports location requests data as CSV
 * @param {Array} requests - Array of request objects
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} actionTypes - Action types for success/fail
 */
export const exportLocationRequestsCSV = (requests, dispatch, actionTypes) => {
  try {
    const dataToExport = requests || [];

    const csvData = dataToExport.map((request) => {
      const deliveryMethods = request.deliveryMethods || [];
      const assigneeInfo = [];

      if (deliveryMethods.includes("email") && request.staffEmail) {
        assigneeInfo.push(request.staffEmail);
      }

      if (deliveryMethods.includes("whatsapp") && request.whatsappNumber) {
        assigneeInfo.push(request.whatsappNumber);
      }

      if (assigneeInfo.length === 0 && request.email) {
        assigneeInfo.push(request.email);
      }

      return {
        ID: request.orderId || "-",
        Status: formatStatus(request.status, request.deliveryMethods),
        "Request Name": request.reference?.title || "-",
        Category: request.reference?.tags?.[0]?.title || "-",
        "Delivery Method": formatDeliveryMethods(request.deliveryMethods),
        "Room Number": request.user?.roomNumber || "-",
        "Last Name": request.user?.name || "-",
        "Request Date": request.createdAt ? new Date(request.createdAt).toLocaleDateString() : "-",
        Assignee: assigneeInfo.join(" - ") || "-",
      };
    });

    const csvContent = convertToCSV(csvData);
    downloadCSV(csvContent, `location-requests-${new Date().toISOString().split("T")[0]}`);

    dispatch({
      type: actionTypes.CSV_SUCCESS,
      payload: csvData,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.CSV_FAIL,
      payload: error.message,
    });
  }
};
